/**
 * 前台用户资料页面
 * 参考 ProfilePage 组件样式重构
 * 黑白主题适配
 */

import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  User as UserIcon,
  Pencil,
  Loader2,
  MapPin,
  Calendar,
  CalendarCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import UserAvatar from "@/components/shared/Avatar";
import EditProfileForm from "@/components/EditProfileForm";
import RandomImage from "@/components/shared/RandomImage";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useUserStore,
  useCurrentUser,
  useUserLoading,
} from "@/stores/userStore";
import { toast } from "sonner";
import { checkInService } from "@/services/checkInService";

/**
 * 用户资料页面
 */
export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const isGlobalLoading = useUserLoading();
  const updateCoverImage = useUserStore((state) => state.updateCoverImage);

  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // 在渲染时直接计算今天是否已签到（优先使用后端返回的 checked_in 字段）
  const isCheckedInToday =
    user?.checked_in ??
    (() => {
      if (!user?.last_check_in_date) return false;
      const today = new Date().toISOString().split("T")[0];
      const lastCheckIn = new Date(user.last_check_in_date)
        .toISOString()
        .split("T")[0];
      return today === lastCheckIn;
    })();

  // 同步本地状态
  useEffect(() => {
    setCheckedIn(isCheckedInToday);
  }, [isCheckedInToday]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      await updateCoverImage(file);
      toast.success(t("profile.upload.success"));
    } catch (err) {
      console.error(err);
      toast.error(t("profile.upload.retryLater"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // 处理签到
  const handleCheckIn = useCallback(async () => {
    if (isCheckedInToday || checkingIn) return;

    try {
      setCheckingIn(true);
      const data = await checkInService.checkIn();
      setCheckedIn(true);
      toast.success(
        data.points !== undefined
          ? t("nav.checkInSuccess", { points: data.points })
          : t("nav.checkInSuccessSimple"),
      );
      // 刷新用户信息以更新积分
      await useUserStore.getState().fetchUser();
    } catch (error) {
      // 错误消息格式: "您今天已经签到过了"
      if (error instanceof Error && error.message.includes("签到")) {
        setCheckedIn(true);
        toast.info(t("nav.checkedIn"));
      } else {
        toast.error(t("nav.checkInFailed"));
      }
    } finally {
      setCheckingIn(false);
    }
  }, [isCheckedInToday, checkingIn, t]);

  // 未登录状态
  if (!user && !isGlobalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-12 max-w-md w-full text-center border-border">
          <UserIcon className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {t("profile.loginRequired.title")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t("profile.loginRequired.description")}
          </p>
          <Button onClick={() => navigate("/login")} className="w-full">
            {t("profile.loginRequired.button")}
          </Button>
        </Card>
      </div>
    );
  }

  // 加载中
  if (!user && isGlobalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return null;

  // 编辑模式
  if (isEditing) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-4">
        <Card className="border-border">
          <CardHeader className="pb-4">
            <h2 className="text-xl font-semibold text-foreground">
              {t("profile.editTitle")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("profile.editDescription")}
            </p>
          </CardHeader>
          <CardContent>
            <EditProfileForm
              user={{
                name: user.name,
                gender: user.gender || null,
                birthday: user.birthday || null,
                bio: user.bio || null,
                location: user.location || null,
                avatarUrl: user.avatar_url || null,
              }}
              onCancel={() => setIsEditing(false)}
              onSaved={() => {
                setIsEditing(false);
              }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // 查看模式
  return (
    <section className="mx-auto max-w-4xl px-4 py-4 md:py-8">
      <Card className="overflow-hidden border-border bg-card rounded-2xl">
        {/* 顶部横幅 - 包含头像、名称、简介 */}
        <CardHeader className="relative h-64 p-0 md:h-72">
          {/* 背景图容器 - 包含图片和渐变 */}
          <div className="absolute inset-0 overflow-hidden">
            {user.cover_image_url ? (
              <img
                src={user.cover_image_url}
                alt="profile-banner"
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <RandomImage width={1200} height={320} />
            )}
            {/* 底部渐变遮罩 */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          </div>

          {/* 修改封面按钮 */}
          <input
            type="file"
            accept="image/*"
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="absolute top-3 right-3 md:top-4 md:right-4 z-20 rounded-lg bg-background/90 hover:bg-background border-border text-foreground"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Pencil className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent sideOffset={6}>
              {t("profile.changeCover")}
            </TooltipContent>
          </Tooltip>

          {/* 头像 + 用户信息（在图片内底部） */}
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <div className="flex items-end gap-4 md:gap-6">
              {/* 头像 */}
              <UserAvatar
                name={user.name}
                src={user.avatar_url}
                size={96}
                className="size-20 md:size-24 ring-4 ring-background/90 dark:ring-background/80 shrink-0 rounded-xl"
              />

              {/* 名称和简介 */}
              <div className="flex-1 min-w-0 pb-1">
                <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight truncate drop-shadow-sm">
                  {user.name}
                </h2>
                <p className="text-white/85 mt-1 text-sm line-clamp-2 drop-shadow-sm">
                  {user.bio || t("profile.defaultBio")}
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-4 pb-6 px-4 md:px-6">
          {/* 标签信息：性别、位置、积分 */}
          <div className="flex flex-wrap items-center gap-2">
            {user.gender && user.gender !== "保密" && (
              <Badge
                variant="outline"
                className="px-2.5 py-1 text-xs border-border text-foreground bg-muted/50"
              >
                {user.gender === "男" || user.gender === "male"
                  ? t("profile.form.gender.male")
                  : user.gender === "女" || user.gender === "female"
                    ? t("profile.form.gender.female")
                    : user.gender}
              </Badge>
            )}
            {user.location && (
              <Badge
                variant="outline"
                className="px-2.5 py-1 text-xs border-border text-foreground bg-muted/50"
              >
                <MapPin className="size-3 mr-1" />
                {user.location}
              </Badge>
            )}
            {typeof user.points === "number" && (
              <Badge
                variant="outline"
                className="px-2.5 py-1 text-xs border-border text-foreground bg-muted/50"
              >
                <svg
                  className="size-3 mr-1"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M24 24L34 14C34 8.47715 29.5228 4 24 4C18.4772 4 14 8.47715 14 14L24 24Z"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14 34L24 24L14 14C8.47715 14 4 18.4772 4 24C4 29.5228 8.47715 34 14 34Z"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M34 34C39.5228 34 44 29.5228 44 24C44 18.4772 39.5228 14 34 14L24 24L34 34Z"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M24 44C29.5228 44 34 39.5228 34 34L24 24L14 34C14 39.5228 18.4772 44 24 44Z"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {user.points} {t("profile.points")}
              </Badge>
            )}
          </div>

          {/* 操作按钮区域 */}
          <div className="mt-4 flex items-center gap-3">
            {/* 签到按钮 */}
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild disabled={isCheckedInToday || checkingIn}>
                <Button
                  size="sm"
                  variant={isCheckedInToday ? "outline" : "default"}
                  className={`flex-1 ${isCheckedInToday ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={handleCheckIn}
                  disabled={isCheckedInToday || checkingIn}
                >
                  {checkingIn ? (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  ) : isCheckedInToday ? (
                    <CalendarCheck className="size-4 mr-2" />
                  ) : (
                    <Calendar className="size-4 mr-2" />
                  )}
                  {isCheckedInToday ? t("nav.checkedIn") : t("nav.checkIn")}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isCheckedInToday
                  ? t("nav.checkedIn")
                  : t("points.howToEarn.checkIn.description")}
              </TooltipContent>
            </Tooltip>

            {/* 编辑资料按钮 */}
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-border"
              onClick={() => setIsEditing(true)}
            >
              {t("profile.editButton")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
